import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildColumns, buildOrderData } from '../../helpers/tableUtil';
import { SortDirection } from '../../models/global';
import { State } from '../../models/state';
import { getAllCountries } from '../../store/actions/country';
import { getUsers } from '../../store/actions/user';
import Button from '../FormComponents/Button';
import Dropdown from '../FormComponents/Dropdown';
import Table from '../Table/Table';
import ToastMessage from '../ToastMessage/ToastMessage';

const tableMap = new Map<string, string>([
  ['Name', 'name'],
  ['Email', 'email'],
  ['Position', 'position'],
  ['Country', 'country'],
  ['Organization', 'organization.name'],
  ['Country', 'country.name'],
  ['Language', 'language'],
  ['Active', 'active'],
]);
const { columns } = buildColumns(tableMap);

function Users() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const rowsPerPage = 10;
  const [page, setPage] = useState(0);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [toast, setToast] = useState('');
  const { users, userCount } = useSelector((state: State) => state.user);
  const { countries: countryFilters } = useSelector((state: State) => state.country.simpleFilters);

  const [pageLoading, setPageLoading] = useState(false);
  const [countryFilter, setCountryFilter] = useState<string>('');

  const [orderBy, setOrderBy] = useState('Email');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    if (!countryFilters.length) {
      dispatch(getAllCountries());
    }
  }, [dispatch, countryFilters.length]);

  useEffect(() => {
    const runOnce = () => {
      setToastType('success');
      setToast(location?.state?.message);
      window.history.replaceState({}, '');
    };
    if (location?.state?.message) {
      runOnce();
    }
  }, [location?.state?.message]);

  useEffect(() => {
    const orderData = buildOrderData({
      orderBy,
      sortDirection,
      tableMap,
    });

    (() => setPageLoading(true))();
    dispatch(
      getUsers(
        {
          limit: rowsPerPage,
          page: page + 1,
          orderData,
          countryFilter,
        },
        () => setPageLoading(false),
      ),
    );
  }, [dispatch, orderBy, page, sortDirection, countryFilter]);

  const goToCreateUser = () => {
    navigate('/users/create');
  };

  const userData = users.map(user => {
    user.country = user.organization?.country?.name;
    if (!user.language && user.organization) {
      user.language = `Same as organization's: ${user.organization.language}`;
    }
    return user;
  });

  if (pageLoading) {
    return (
      <Box className='loader'>
        <CircularProgress size={40} color='inherit' />
      </Box>
    );
  }
  return (
    <>
      <Box className='list'>
        <Box className='list-header' sx={{ mb: 2 }}>
          <Typography variant='h5'>Users</Typography>
          <Box className='create-button'>
            <Button text='Create' onClick={goToCreateUser} />
          </Box>
        </Box>

        <Box className='simple-filters'>
          <Dropdown
            value={countryFilter}
            label='Country'
            options={countryFilters}
            onChange={(_, v: string) => setCountryFilter(v)}
          />
        </Box>

        <Table
          columns={columns}
          rows={userData}
          count={userCount}
          page={page}
          rowsPerPage={rowsPerPage}
          setPage={setPage}
          rowClickHandler={(id: string) => navigate(`/users/${id}`)}
          orderBy={orderBy}
          handleOrder={setOrderBy}
          sortDirection={sortDirection}
          handleSortDirection={setSortDirection}
        />
      </Box>
      <ToastMessage toast={toast} close={() => setToast('')} messageType={toastType} />
    </>
  );
}

export default Users;
